import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import ContactForm from '../../components/ContactForm';
import Icon from '../../components/AppIcon';

const DevelopersPage = () => {
  const handleDeveloperWaitlistSuccess = (data) => {
    console.log('Developer waitlist form submitted successfully:', data);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Developers - Solis Games</title>
        <meta name="description" content="Build WebGPU games with realtime leaderboards, tournaments, achievements, friends system, Discord-style chat, analytics, and monetization. Add competitive gaming, Steam-style social features, AI-moderated messaging, and Xbox Live-style progression in minutes with our comprehensive SDK." />
      </Helmet>
      
      <Header />
      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="container mx-auto px-4 lg:px-8 py-8 max-w-6xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Code" size={40} className="text-primary" />
            </div>
            <h1 className="text-5xl font-bold text-foreground mb-6">
              Build the Future of Gaming
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Join Solis Games and bring your WebGPU games to millions of players.
              Add <span className="text-orange-500 font-semibold">realtime leaderboards</span>, <span className="text-pink-500 font-semibold">tournaments</span>, <span className="text-amber-500 font-semibold">achievements</span>, <span className="text-cyan-500 font-semibold">Steam-style friends</span>, and <span className="text-indigo-500 font-semibold">Discord-style chat</span> in minutes,
              plus analytics, monetization, and cross-device features—all with zero backend work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link
                to="/studio-dashboard"
                className="inline-flex items-center px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                <Icon name="LayoutDashboard" size={20} className="mr-2" />
                Studio Portal
              </Link>
              <Link
                to="/studio-sdk"
                className="inline-flex items-center px-8 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors"
              >
                <Icon name="BookOpen" size={20} className="mr-2" />
                View Documentation
              </Link>
            </div>
          </div>

          {/* Analytics Intelligence Callout */}
          <div className="mb-8">
            <Link
              to="/analytics-showcase"
              className="block bg-gradient-to-br from-purple-500/10 via-primary/10 to-background border-2 border-purple-500/30 rounded-2xl p-8 hover:border-purple-500/50 transition-all group"
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-20 h-20 bg-purple-500/20 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Icon name="BarChart3" size={40} className="text-purple-500" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 border border-purple-500/30 rounded-full mb-3">
                    <Icon name="Sparkles" size={16} className="text-purple-500" />
                    <span className="text-sm font-semibold text-purple-500">EXCLUSIVE TO SOLIS</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                    Studio Intelligence Hub
                  </h3>
                  <p className="text-lg text-muted-foreground">
                    Advanced analytics intelligence that <span className="text-foreground font-semibold">Steam, Epic & itch.io don't offer</span>.
                    Cohort retention heat maps, competitive benchmarking, player journey funnels, and more.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-purple-500 font-semibold group-hover:gap-3 transition-all">
                  <span>Explore Analytics</span>
                  <Icon name="ArrowRight" size={20} />
                </div>
              </div>
            </Link>
          </div>

          {/* Monetization System Callout */}
          <div className="mb-16">
            <Link
              to="/developers/monetization"
              className="block bg-gradient-to-br from-green-500/10 via-emerald-500/10 to-background border-2 border-green-500/30 rounded-2xl p-8 hover:border-green-500/50 transition-all group"
            >
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="w-20 h-20 bg-green-500/20 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Icon name="DollarSign" size={40} className="text-green-600" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full mb-3">
                    <Icon name="TrendingUp" size={16} className="text-green-600" />
                    <span className="text-sm font-semibold text-green-600">BEST REVENUE SHARE</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                    Revenue & Monetization
                  </h3>
                  <p className="text-lg text-muted-foreground">
                    Keep <span className="text-foreground font-semibold">80% of all revenue</span> with 20% platform fee.
                    Variable IAP pricing, automated payouts, unified revenue tracking—earn $1,000 more per $10k vs competitors.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-green-600 font-semibold group-hover:gap-3 transition-all">
                  <span>View Monetization</span>
                  <Icon name="ArrowRight" size={20} />
                </div>
              </div>
            </Link>
          </div>

          {/* Integration Options Section */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-3">Choose Your Integration</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Integrate Solis Games into your project with the method that works best for you
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* JavaScript SDK */}
              <div className="bg-gradient-to-br from-blue-500/5 to-cyan-500/5 border-2 border-blue-500/20 rounded-xl p-8 hover:border-blue-500/40 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mr-4">
                      <Icon name="Code" size={24} className="text-blue-500" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">JavaScript SDK</h3>
                      <p className="text-sm text-muted-foreground">For web games and HTML5 projects</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <Icon name="Check" size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Simple script tag integration</span>
                  </div>
                  <div className="flex items-start">
                    <Icon name="Check" size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Works with any web framework</span>
                  </div>
                  <div className="flex items-start">
                    <Icon name="Check" size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">No build tools required</span>
                  </div>
                  <div className="flex items-start">
                    <Icon name="Check" size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Best for: Phaser, PixiJS, Three.js, vanilla JS games</span>
                  </div>
                </div>

                <div className="bg-background/50 rounded-lg p-4 mb-4">
                  <p className="text-sm font-mono text-foreground">
                    {'<script src="https://solisgames.com/solis-games-sdk-v1.js"></script>'}
                  </p>
                </div>

                <Link
                  to="/studio-sdk?tab=javascript"
                  className="inline-flex items-center justify-center w-full px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors"
                >
                  <Icon name="BookOpen" size={20} className="mr-2" />
                  View JavaScript Docs
                </Link>
              </div>

              {/* Unity Plugin */}
              <div className="bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-2 border-purple-500/20 rounded-xl p-8 hover:border-purple-500/40 transition-all relative">
                <div className="absolute top-4 right-4">
                  <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                    NEW
                  </span>
                </div>

                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mr-4">
                      <Icon name="Box" size={24} className="text-purple-500" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">Unity Plugin</h3>
                      <p className="text-sm text-muted-foreground">Native C# SDK for Unity WebGL</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <Icon name="Check" size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Type-safe C# API with IntelliSense</span>
                  </div>
                  <div className="flex items-start">
                    <Icon name="Check" size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Full async/await support</span>
                  </div>
                  <div className="flex items-start">
                    <Icon name="Check" size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Editor testing with mock data</span>
                  </div>
                  <div className="flex items-start">
                    <Icon name="Check" size={18} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-muted-foreground">Best for: Unity 2022.3+ WebGL projects</span>
                  </div>
                </div>

                <div className="bg-background/50 rounded-lg p-4 mb-4">
                  <p className="text-sm font-mono text-foreground">
                    await SolisSDK.InitAsync(apiKey);
                  </p>
                </div>

                <div className="flex gap-3">
                  <Link
                    to="/studio-sdk?tab=unity"
                    className="inline-flex items-center justify-center flex-1 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
                  >
                    <Icon name="BookOpen" size={20} className="mr-2" />
                    View Unity Docs
                  </Link>
                  <a
                    href="https://github.com/solis-interactive/solisgames-sdk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-3 border border-border hover:bg-muted text-foreground rounded-lg font-semibold transition-colors"
                  >
                    <Icon name="Github" size={20} />
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-muted/30 border border-border rounded-lg p-6 text-center">
              <p className="text-muted-foreground">
                <Icon name="Info" size={16} className="inline mr-2 text-blue-500" />
                Both integration methods provide access to the same features: leaderboards, tournaments, achievements, friends, chat, analytics, monetization, and more.
              </p>
            </div>
          </div>

          {/* SDK Features Section */}
          <div className="mb-16">
            <div className="bg-card border border-border rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="Sparkles" size={24} className="text-blue-500" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">Platform Features</h2>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                Access all these features regardless of which SDK you choose.
                Get started in minutes with comprehensive documentation and code examples.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div className="bg-background border border-border rounded-lg p-6 text-center">
                  <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="Shield" size={24} className="text-green-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Site Lock</h3>
                  <p className="text-sm text-muted-foreground">
                    Protect your games from unauthorized hosting with automatic domain validation
                  </p>
                </div>

                <Link to="/analytics-showcase" className="bg-gradient-to-br from-purple-500/10 to-primary/10 border-2 border-purple-500/20 rounded-lg p-6 text-center hover:border-purple-500/40 transition-all relative group">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-purple-500 text-white rounded-full flex items-center gap-1">
                      <Icon name="Sparkles" size={12} />
                      EXCLUSIVE
                    </span>
                  </div>
                  <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Icon name="BarChart3" size={24} className="text-purple-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Studio Intelligence Hub</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Advanced analytics intelligence that Steam, Epic & itch.io don't offer
                  </p>
                  <div className="flex items-center justify-center gap-1 text-xs text-purple-500 font-medium">
                    <span>Learn More</span>
                    <Icon name="ArrowRight" size={14} />
                  </div>
                </Link>

                <div className="bg-background border border-border rounded-lg p-6 text-center">
                  <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="DollarSign" size={24} className="text-yellow-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Monetization</h3>
                  <p className="text-sm text-muted-foreground">
                    Rewarded ads, interstitials, banners, and in-app purchases integration
                  </p>
                </div>

                <div className="bg-background border border-border rounded-lg p-6 text-center">
                  <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="Cloud" size={24} className="text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Cloud Saves</h3>
                  <p className="text-sm text-muted-foreground">
                    Cross-device game progress storage with automatic synchronization
                  </p>
                </div>

                <div className="bg-background border border-border rounded-lg p-6 text-center relative">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="Trophy" size={24} className="text-orange-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Leaderboards</h3>
                  <p className="text-sm text-muted-foreground">
                    Realtime competitive leaderboards with automatic anti-cheat and rank tracking
                  </p>
                </div>

                <div className="bg-background border border-border rounded-lg p-6 text-center relative">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <div className="w-12 h-12 bg-pink-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="Award" size={24} className="text-pink-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Tournaments</h3>
                  <p className="text-sm text-muted-foreground">
                    Automated tournament brackets with prize distribution and player matchmaking
                  </p>
                </div>

                <div className="bg-background border border-border rounded-lg p-6 text-center relative">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="Star" size={24} className="text-amber-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Achievements</h3>
                  <p className="text-sm text-muted-foreground">
                    Xbox Live-style achievements with cross-game XP progression and automatic rarity
                  </p>
                </div>

                <div className="bg-background border border-border rounded-lg p-6 text-center relative">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="Users" size={24} className="text-cyan-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Friends & Social</h3>
                  <p className="text-sm text-muted-foreground">
                    Platform-wide friends system with real-time presence and cross-game social graph
                  </p>
                </div>

                <div className="bg-background border border-border rounded-lg p-6 text-center relative">
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <div className="w-12 h-12 bg-indigo-500/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Icon name="MessageSquare" size={24} className="text-indigo-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">In-Game Chat</h3>
                  <p className="text-sm text-muted-foreground">
                    Discord-style chat with AI moderation, ephemeral messages, and realtime delivery
                  </p>
                </div>
              </div>

              <div className="bg-muted/30 rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3">Quick Integration Example:</h4>
                <pre className="bg-background border border-border rounded p-4 text-sm overflow-x-auto">
                  <code>{`<!-- Add to your HTML -->
<script src="https://solisgames.com/solis-games-sdk-v1.js"></script>

<script>
  // Initialize SDK
  await window.SolisGames.SDK.init('your_api_key');

  // Track gameplay
  window.SolisGames.SDK.game.gameplayStart();

  // Submit score to leaderboard (NEW!)
  await window.SolisGames.SDK.leaderboards.submit('high_scores', {
    score: 9500,
    metadata: { level: 5, time: 120 }
  });

  // Unlock achievement (NEW!)
  await window.SolisGames.SDK.achievements.unlock('first_win');

  // Listen for rank changes (NEW!)
  window.SolisGames.SDK.leaderboards.onRankChange((data) => {
    console.log(\`You're now rank #\${data.rank}!\`);
  });

  // Show rewarded ad
  const result = await window.SolisGames.SDK.ad.requestRewarded();
  if (result.success && result.reward) {
    givePlayerReward();
  }
</script>`}</code>
                </pre>
              </div>
            </div>
          </div>

          {/* Social & Competition Section */}
          <div className="mb-16">
            <div className="bg-gradient-to-br from-orange-500/5 via-pink-500/5 to-purple-500/5 border border-orange-500/20 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="Trophy" size={24} className="text-orange-500" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-3xl font-bold text-foreground">Leaderboards & Tournaments</h2>
                    <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add competitive gaming to your titles in minutes • CrazyGames & Kongregate don't have this
                  </p>
                </div>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                Turn your single-player games into competitive experiences with zero backend work.
                Our realtime leaderboards and automated tournament system scale to millions of players.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Trophy" size={20} className="text-orange-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Realtime Leaderboards</h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>One-line score submission - auto-creates leaderboards</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Realtime WebSocket rank updates for live competition</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Multiple scopes: global, daily, weekly, monthly</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Server-side anti-cheat with Z-score outlier detection</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Sub-100ms response times with edge caching</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-pink-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Award" size={20} className="text-pink-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Automated Tournaments</h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Auto-generate brackets: single/double elimination, Swiss, round robin</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Scheduled tournaments with automatic start/end times</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Prize pools with automatic coin distribution</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Entry fees and participant management built-in</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Match tracking and winner determination</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Code" size={20} className="mr-2" />
                  Code Example - Add Competitive Gaming in 5 Lines:
                </h4>
                <pre className="bg-background border border-border rounded p-4 text-sm overflow-x-auto">
                  <code>{`// Submit score (auto-creates leaderboard if it doesn't exist)
await SolisGames.SDK.leaderboards.submit('high_scores', { score: 9500 });

// Get top 10
const top10 = await SolisGames.SDK.leaderboards.get('high_scores', { limit: 10 });

// Create weekend tournament
await SolisGames.SDK.tournaments.create({
  name: 'Weekend Blitz',
  durationHours: 72,
  prizePool: { '1st': 10000, '2nd': 5000, '3rd': 2500 }
});`}</code>
                </pre>
              </div>
            </div>
          </div>

          {/* Achievements Section */}
          <div className="mb-16">
            <div className="bg-gradient-to-br from-amber-500/5 via-yellow-500/5 to-orange-500/5 border border-amber-500/20 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="Star" size={24} className="text-amber-500" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-3xl font-bold text-foreground">Achievements & Progression</h2>
                    <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Xbox Live-style achievements with cross-game XP • CrazyGames & Kongregate don't have this
                  </p>
                </div>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                Boost player engagement and retention with beautiful achievement unlocks and cross-game progression.
                Players earn XP across all your games, building their profile and staying invested in your content.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Star" size={20} className="text-amber-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Achievement System</h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>One-line unlock - define achievements in code, unlock instantly</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Progress tracking for incremental achievements (75/100 coins)</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Automatic rarity calculation: Common, Uncommon, Rare</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Beautiful unlock animations with callbacks for custom UI</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Unlock rates and rarity stats updated hourly</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-yellow-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Zap" size={20} className="text-yellow-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Cross-Game XP Progression</h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Players earn profile XP across all games on your studio</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Level system: Level 1 = 100 XP, Level 50 = 250,000 XP</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Track completion percentage per game</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Auto-unlock when progress reaches target</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Level-up detection for celebratory moments</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Code" size={20} className="mr-2" />
                  Code Example - Add Achievements in 3 Lines:
                </h4>
                <pre className="bg-background border border-border rounded p-4 text-sm overflow-x-auto">
                  <code>{`// Define achievements for your game
SolisGames.SDK.achievements.define([
  { id: 'first_win', name: 'First Victory', xp: 100 },
  { id: 'speedrunner', name: 'Speedrunner', xp: 500, rare: true }
]);

// Unlock achievement (auto-grants XP, calculates rarity)
await SolisGames.SDK.achievements.unlock('first_win');

// Track progress (auto-unlocks at 100%)
await SolisGames.SDK.achievements.progress('collect_coins', { current: 75, target: 100 });`}</code>
                </pre>
              </div>
            </div>
          </div>

          {/* Friends & Social Graph Section */}
          <div className="mb-16">
            <div className="bg-gradient-to-br from-blue-500/5 via-cyan-500/5 to-teal-500/5 border border-blue-500/20 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="Users" size={24} className="text-blue-500" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-3xl font-bold text-foreground">Friends & Social Graph</h2>
                    <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Platform-wide friends system with real-time presence • CrazyGames & Kongregate don't have this
                  </p>
                </div>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                Build Steam-style social features into your games with zero backend work. Friends work across all games and studios on Solis Games,
                creating powerful network effects and keeping players engaged across your entire catalog.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Users" size={20} className="text-blue-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Cross-Game Friends</h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Platform-wide social graph like Steam/Xbox Live</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Friend requests with auto-accept logic</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Automatic bidirectional friendships via database triggers</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Friends list sorted by online status (playing &gt; online &gt; away &gt; offline)</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Block/unblock users with automatic friendship removal</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Activity" size={20} className="text-green-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Real-Time Presence</h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>See what friends are playing in real-time</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Custom status messages (e.g., "Level 5 - Boss Fight")</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Joinable sessions for multiplayer games</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Automatic heartbeat keeps status fresh (30s updates)</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Activity feed showing friend achievements, scores, games played</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Code" size={20} className="mr-2" />
                  Code Example - Add Social Features in 5 Lines:
                </h4>
                <pre className="bg-background border border-border rounded p-4 text-sm overflow-x-auto">
                  <code>{`// Send friend request
await SolisGames.SDK.friends.sendRequest('user_uuid', 'Let\\'s play together!');

// Get friends list with presence
const friends = await SolisGames.SDK.friends.list();

// Update presence when playing
await SolisGames.SDK.presence.set('playing', {
  customStatus: 'Level 5 - Boss Fight',
  joinable: true
});

// Get friend activity feed
const feed = await SolisGames.SDK.social.getFeed({ limit: 20 });`}</code>
                </pre>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6 mt-6">
                <div className="flex items-start">
                  <Icon name="Info" size={20} className="text-blue-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Platform-Level Network Effects</h4>
                    <p className="text-muted-foreground text-sm">
                      Friends work across <strong>ALL games and studios</strong> on Solis Games (like Steam/Xbox Live).
                      When players become friends, they stay connected across your entire catalog and even other studios' games.
                      This creates powerful network effects, increases platform stickiness, and drives cross-game discovery.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* In-Game Chat & Messaging Section */}
          <div className="mb-16">
            <div className="bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 border border-indigo-500/20 rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-indigo-500/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="MessageSquare" size={24} className="text-indigo-500" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-3xl font-bold text-foreground">In-Game Chat & Messaging</h2>
                    <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-primary to-purple-500 text-white rounded-full">
                      NEW
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Discord-style chat with AI moderation • CrazyGames & Kongregate don't have this
                  </p>
                </div>
              </div>

              <p className="text-lg text-muted-foreground mb-8">
                Add multiplayer communication to your games with zero infrastructure work.
                Our chat system includes AI-powered toxicity detection, automatic spam protection, and 24-hour ephemeral messages
                that scale to thousands of concurrent users per game.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-indigo-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="MessageSquare" size={20} className="text-indigo-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Multiple Channel Types</h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Game lobbies - match-specific chat rooms</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Global channels - cross-game communication</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Team channels - private group chat</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Direct messages - 1-on-1 private chat</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Participant management with owner/moderator/member roles</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Shield" size={20} className="text-purple-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">AI Auto-Moderation</h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Perspective API toxicity detection (Google)</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Auto-filter messages with toxicity score &gt; 0.9</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Flag for review if score &gt; 0.7 (configurable thresholds)</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Custom word filters per game</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Rate limiting (5 messages/minute) with anti-spam protection</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-pink-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Zap" size={20} className="text-pink-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Realtime & Scalable</h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>WebSocket-based realtime message delivery (&lt; 200ms latency)</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Scales to 10,000+ concurrent users per game</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>24-hour ephemeral messages (auto-delete after 24hrs)</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Message threading with reply support</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>System messages for events (user joined, left, etc.)</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-red-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Ban" size={20} className="text-red-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Moderation Tools</h3>
                  </div>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Temporary, permanent, and shadow bans</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Platform-wide, game-wide, or channel-specific bans</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Automatic ban expiration with notification</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Message deletion and edit history</span>
                    </li>
                    <li className="flex items-start">
                      <Icon name="Check" size={16} className="text-green-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Flagged message review dashboard for studios</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-background/80 backdrop-blur border border-border rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Code" size={20} className="mr-2" />
                  Code Example - Add Chat in 5 Lines:
                </h4>
                <pre className="bg-background border border-border rounded p-4 text-sm overflow-x-auto">
                  <code>{`// List channels for your game
const channels = await SolisGames.SDK.chat.listChannels('your_game_id');

// Create a lobby channel
const lobby = await SolisGames.SDK.chat.createChannel('your_game_id', {
  name: 'Lobby #1',
  channelType: 'game_lobby',
  maxParticipants: 10
});

// Join channel
await SolisGames.SDK.chat.joinChannel(lobby.channel.id);

// Send message (auto-moderated by AI)
await SolisGames.SDK.chat.send(lobby.channel.id, 'Hello everyone!');

// Listen for realtime messages
const unsubscribe = SolisGames.SDK.chat.onMessage(lobby.channel.id, (message) => {
  console.log(\`\${message.user.fullName}: \${message.message}\`);
  if (message.isFlagged) {
    console.log('Message flagged for:', message.flagReason);
  }
});

// Get chat history (paginated)
const history = await SolisGames.SDK.chat.getHistory(lobby.channel.id, { limit: 50 });`}</code>
                </pre>
              </div>
            </div>
          </div>

          {/* Studio Dashboard Section */}
          <div className="mb-16">
            <div className="bg-card border border-border rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="LayoutDashboard" size={24} className="text-purple-500" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">Studio Dashboard</h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                Manage your games, track performance, and monitor revenue with our comprehensive studio portal.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="TrendingUp" size={16} className="text-green-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Real-time Analytics</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Monitor player engagement, session length, retention rates, and revenue metrics 
                    with live dashboard updates and detailed reporting.
                  </p>
                </div>

                <div className="bg-background border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Key" size={16} className="text-blue-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">API Management</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Generate and manage API keys, configure domain whitelists for testing, 
                    and control SDK permissions with granular access controls.
                  </p>
                </div>

                <div className="bg-background border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="CreditCard" size={16} className="text-orange-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Revenue Tracking</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Track ad revenue, in-app purchases, and revenue sharing with automated 
                    payouts via Stripe and detailed financial reporting.
                  </p>
                </div>

                <div className="bg-background border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="BookOpen" size={16} className="text-purple-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">SDK Documentation</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Complete integration guides, code examples, and API reference 
                    built right into the studio portal for easy access.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Monetization Section */}
          <div className="mb-16">
            <div className="bg-card border border-border rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="DollarSign" size={24} className="text-green-500" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">Multiple Revenue Streams</h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                Maximize your game's earning potential with our comprehensive monetization toolkit.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-background border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-red-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="Play" size={16} className="text-red-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Video Ads</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Rewarded video ads, interstitials, and banner ads with automatic optimization 
                    and revenue sharing based on your game's performance.
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Rewarded video ads for in-game rewards</li>
                    <li>• Interstitial ads between levels</li>
                    <li>• Banner ads with flexible positioning</li>
                  </ul>
                </div>

                <div className="bg-background border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="ShoppingCart" size={16} className="text-blue-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">In-App Purchases</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Complete IAP system with Stripe integration for virtual items, currency, 
                    and premium features with automatic revenue tracking.
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Virtual currency and items</li>
                    <li>• Premium features and upgrades</li>
                    <li>• Subscription management</li>
                  </ul>
                </div>

                <div className="bg-background border border-border rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-orange-500/10 rounded-lg flex items-center justify-center mr-3">
                      <Icon name="TrendingUp" size={16} className="text-orange-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground">Revenue Sharing</h3>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Earn from platform subscription revenue based on your game's popularity, 
                    engagement metrics, and player retention rates.
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Performance-based revenue sharing</li>
                    <li>• Monthly automated payouts</li>
                    <li>• Transparent revenue reporting</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Developer Tools Section */}
          <div className="mb-16">
            <div className="bg-card border border-border rounded-xl p-8">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center mr-4">
                  <Icon name="Gamepad2" size={24} className="text-orange-500" />
                </div>
                <h2 className="text-3xl font-bold text-foreground">Unity 6 Development Tools</h2>
              </div>
              
              <p className="text-lg text-muted-foreground mb-8">
                We believe creating instant games should be fun and intuitive. Our Unity 6 integration 
                tools make building WebGPU games effortless and enjoyable.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-background border border-border rounded-lg p-4 text-center">
                  <div className="w-10 h-10 bg-pink-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="Volume2" size={20} className="text-pink-500" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">Sound FX Generation</h4>
                  <p className="text-sm text-muted-foreground">AI-powered audio tools for creating immersive game soundscapes</p>
                </div>

                <div className="bg-background border border-border rounded-lg p-4 text-center">
                  <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="Palette" size={20} className="text-cyan-500" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">Artwork Generation</h4>
                  <p className="text-sm text-muted-foreground">Create stunning visuals and vector assets with our integrated art tools</p>
                </div>

                <div className="bg-background border border-border rounded-lg p-4 text-center">
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="ShoppingCart" size={20} className="text-green-500" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">IAP Stack</h4>
                  <p className="text-sm text-muted-foreground">Complete in-app purchase system with analytics and revenue optimization</p>
                </div>

                <div className="bg-background border border-border rounded-lg p-4 text-center">
                  <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Icon name="Zap" size={20} className="text-purple-500" />
                  </div>
                  <h4 className="font-semibold text-foreground mb-2">Level Generation</h4>
                  <p className="text-sm text-muted-foreground">Procedural content generation for infinite replayability and engagement</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-primary/10 to-purple-500/10 border border-primary/20 rounded-xl p-8 text-center mb-16">
            <h2 className="text-3xl font-bold text-foreground mb-4">Ready to Get Started?</h2>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Our SDK is ready for production use! Create your studio account, get your API key, 
              and start integrating your games with our platform today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/studio-dashboard"
                className="inline-flex items-center px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                <Icon name="Rocket" size={20} className="mr-2" />
                Start Building Now
              </Link>
              <Link
                to="/studio-sdk"
                className="inline-flex items-center px-8 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors"
              >
                <Icon name="BookOpen" size={20} className="mr-2" />
                View SDK Docs
              </Link>
            </div>
          </div>

          {/* Developer Waitlist Form */}
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-4">Developer Waitlist Application</h2>
              <p className="text-muted-foreground">
                Tell us about your game development experience and what you'd like to build on our platform.
              </p>
            </div>
            
            <ContactForm
              onSuccess={handleDeveloperWaitlistSuccess}
              initialIssueType="developer_waitlist"
              sourcePage="Developers"
              sourceUrl="/developers"
              metadata={{ 
                formType: 'developer_waitlist',
                referrer: 'developers_page'
              }}
              showTitle={false}
            />
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default DevelopersPage;
